import React, { useState, useEffect } from 'react';
import { NavLink, Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Bell, Plus, LogOut, ChevronDown, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import XpertLogo, { XpertWordmark } from '../components/XpertLogo';
import { dashboardNav } from '../data/mock';
import DashboardHome from '../components/dashboard/DashboardHome';
import SchedulePage from '../components/dashboard/SchedulePage';
import ServicePlansPage from '../components/dashboard/ServicePlansPage';
import PesticideLogPage from '../components/dashboard/PesticideLogPage';
import DevicesPage from '../components/dashboard/DevicesPage';
import AuditPackPage from '../components/dashboard/AuditPackPage';
import ServiceTracker from '../components/dashboard/ServiceTracker';
import CustomersPage from '../components/dashboard/CustomersPage';
import CustomerDetail from '../components/dashboard/CustomerDetail';
import RoutesPage from '../components/dashboard/RoutesPage';
import InvoicesQuickView from '../components/dashboard/InvoicesQuickView';
import ReportsPage from '../components/dashboard/ReportsPage';
import MarketingPage from '../components/dashboard/MarketingPage';
import SettingsPage from '../components/dashboard/SettingsPage';
import AccountingApp from '../components/accounting/AccountingApp';
import LeadsPage from '../components/dashboard/LeadsPage';
import ContractsPage from '../components/dashboard/ContractsPage';
import FormsPage from '../components/dashboard/FormsPage';
import InventoryPage from '../components/dashboard/InventoryPage';
import CommunicationHub from '../components/dashboard/CommunicationHub';
import EducationPage from '../components/dashboard/EducationPage';
import TemplatesPage from '../components/dashboard/TemplatesPage';
import MapPage from '../components/dashboard/MapPage';
import PaymentsPage from '../components/dashboard/PaymentsPage';
import CollectionsPage from '../components/dashboard/CollectionsPage';
import PestLibrary from '../components/dashboard/PestLibrary';
import AiAssistant from '../components/dashboard/AiAssistant';
import EndOfDay from '../components/dashboard/EndOfDay';
import DataHub from '../components/dashboard/DataHub';
import KnowledgeAutoUpdate from '../components/dashboard/KnowledgeAutoUpdate';
import BusinessBriefing from '../components/dashboard/BusinessBriefing';
import ServiceInstructions from '../components/dashboard/ServiceInstructions';
import PesticideReports from '../components/dashboard/PesticideReports';
import AssistantsPanel from '../components/dashboard/AssistantsPanel';
import GlobalSearch from '../components/dashboard/GlobalSearch';

import { messaging } from '../lib/api';

// Which permission section (see PERMISSION_SECTIONS in server.py) governs each
// menu item for a "staff" login. The API serves staff only the sections they
// were granted, and every other screen here is admin-only on the server, so
// a staff menu shows just these — plus the read-only reference pages.
const STAFF_SECTION_FOR = {
  schedule: 'schedule', tracker: 'tracker', recurring: 'contracts', map: 'map',
  customers: 'customers', leads: 'leads', contracts: 'contracts', forms: 'forms',
  inventory: 'inventory', pesticide: 'pesticide', routes: 'routes',
  invoices: 'invoices', payments: 'payments', collections: 'invoices',
  accounting: 'accounting', messaging: 'messaging',
};
const STAFF_ALWAYS = new Set(['dashboard', 'pest-library', 'knowledge', 'education']);
export const visibleNav = (u) => {
  if (!u || u.role !== 'staff') return dashboardNav;
  const granted = new Set(u.permissions || []);
  return dashboardNav.filter((item) => STAFF_ALWAYS.has(item.path) || granted.has(STAFF_SECTION_FOR[item.path]));
};

const SidebarInner = ({ collapsed, unread, onNavigate }) => {
  let orgName = 'Xpertpests Pro';
  let navItems = dashboardNav;
  try {
    const raw = localStorage.getItem('pp_user');
    const u = raw ? (JSON.parse(raw) || {}) : {};
    orgName = u.orgName || orgName;
    navItems = visibleNav(u);
  } catch { /* fall back to the product name */ }
  return (
  <>
    <Link to="/app/dashboard" onClick={onNavigate} className="h-[64px] flex items-center gap-2 px-4 border-b border-white/10 flex-shrink-0">
      <XpertLogo size={36} light/>
      {!collapsed && <XpertWordmark light size="sm"/>}
    </Link>
    <nav className="flex-1 py-3 px-2.5 space-y-0.5 overflow-y-auto">
      {navItems.map(item => {
        const Ico = Icons[item.icon] || Icons.Circle;
        const showBadge = item.path === 'messaging' && unread > 0;
        return (
          <NavLink key={item.path} to={`/app/${item.path}`} onClick={onNavigate} className={({isActive}) => `relative flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${isActive ? 'bg-[#8BC53F] text-white' : 'text-white/70 hover:text-white hover:bg-white/5'}`}>
            <Ico className="w-4 h-4 flex-shrink-0"/>
            {!collapsed && <span>{item.name}</span>}
            {showBadge && (
              <span className={`${collapsed ? 'absolute top-1 right-1' : 'ml-auto'} inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-[#F59E0B] text-white text-[10px] font-bold`} data-testid="sidebar-unread-badge">{unread}</span>
            )}
          </NavLink>
        );
      })}
    </nav>
    <div className="p-4 border-t border-white/10 flex-shrink-0">
      {!collapsed ? (
        <div className="text-xs text-white/50">
          <div className="font-semibold text-white/70 mb-0.5" data-testid="sidebar-org-name">{orgName}</div>
          v2026.1.4
        </div>
      ) : null}
    </div>
  </>
  );
};

// Which layout is on screen. The top bar has one menu button, but it means two
// different things: on a wide window it collapses the sidebar, and on a narrow
// one it opens the slide-in drawer. Driving both from the same click let the two
// states drift apart, and a drawer left open on a wide window is invisible
// (lg:hidden) yet still mounted — so narrowing the window, or zooming in past
// the 1024px breakpoint, brought it back as a full-screen overlay that swallowed
// every click on the page. Reading the breakpoint means each click moves only
// the state that layout actually uses.
const LG = '(min-width: 1024px)';
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window === 'undefined' || window.matchMedia(LG).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(LG);
    const onChange = (e) => setIsDesktop(e.matches);
    setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
};

// A dropdown or dialog from the component library sets `pointer-events: none`
// on <body> while it is open, and takes it off again when it closes itself. If
// the page navigates while one is open — the browser's Back button, or any
// redirect that is not the menu item being clicked — the menu is unmounted
// without ever closing, so nothing takes the style off. The screen then looks
// completely normal and ignores every click, on the sidebar, the tabs and
// everything else: the whole page is dead with no sign of why.
//
// Clearing it on each route change is safe because no menu or dialog here
// survives a navigation; anything still holding the body open has already gone.
const useReleaseStuckClicks = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.body.style.pointerEvents === 'none') {
      document.body.style.removeProperty('pointer-events');
    }
    // The menu's own container is left behind with it, and it sits above the
    // page, so it has to go too or it keeps catching the clicks itself.
    document
      .querySelectorAll('[data-radix-popper-content-wrapper]')
      .forEach((el) => el.remove());
  }, [pathname]);
};


// Desktop sidebar — inline, collapsible (hidden on mobile)
const Sidebar = ({ collapsed, unread }) => (
  <aside className={`${collapsed ? 'w-[68px]' : 'w-[232px]'} hidden lg:flex transition-all duration-200 bg-[#0d2818] text-white flex-col min-h-screen sticky top-0`}>
    <SidebarInner collapsed={collapsed} unread={unread}/>
  </aside>
);

// Mobile slide-in drawer + overlay (hidden on desktop)
const MobileDrawer = ({ open, unread, onClose }) => (
  <div className={`lg:hidden fixed inset-0 z-50 ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open}>
    <div onClick={onClose} className={`absolute inset-0 bg-black/50 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0'}`} data-testid="mobile-drawer-overlay"/>
    <aside className={`absolute inset-y-0 left-0 w-[260px] bg-[#0d2818] text-white flex flex-col transition-transform duration-200 ${open ? 'translate-x-0' : '-translate-x-full'}`} data-testid="mobile-drawer">
      <SidebarInner unread={unread} onNavigate={onClose}/>
    </aside>
  </div>
);

const TopBar = ({ user, onLogout, onToggle }) => {
  const nav = useNavigate();
  // An owner who also runs jobs (has a Tech ID) can jump to their own day in the field app.
  const fieldTech = !!user?.techId;
  return (
  <header className="h-[64px] bg-white border-b border-slate-200 flex items-center px-3 sm:px-6 gap-2 sm:gap-4 sticky top-0 z-30">
    {/* The only way to navigate on a phone, and it was a 20x20 target - the
        icon with no padding around it. Apple and Google both ask for 44.
        Measured at 390x844: 20x20 before, 44x44 after. */}
    <button onClick={onToggle} aria-label="Open menu"
      className="-ml-1 w-11 h-11 flex items-center justify-center rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 flex-shrink-0"
      data-testid="topbar-menu-btn"><Icons.Menu className="w-6 h-6"/></button>
    <div className="flex-1 max-w-xl min-w-0">
      <GlobalSearch/>
    </div>
    <Button onClick={() => nav('/app/customers')} size="sm" aria-label="New customer"
      className="bg-[#8BC53F] hover:bg-[#6FA82C] text-white font-semibold gap-1.5 flex-shrink-0 h-11 w-11 sm:h-9 sm:w-auto sm:px-3"
      data-testid="topbar-new-btn"><Plus className="w-5 h-5 sm:w-4 sm:h-4"/><span className="hidden sm:inline">New</span></Button>
    {fieldTech && <Button asChild size="sm" variant="outline" className="flex-shrink-0 h-11 sm:h-9 gap-1.5 font-semibold" data-testid="topbar-myday-btn"><Link to="/tech"><Icons.Truck className="w-4 h-4"/><span className="hidden sm:inline">My Day</span></Link></Button>}
    <button onClick={() => nav('/app/messaging')} title="Messages" aria-label="Messages" className="relative w-10 h-10 rounded-full hover:bg-slate-100 hidden sm:flex items-center justify-center" data-testid="topbar-bell-btn">
      <Bell className="w-5 h-5 text-slate-600"/>
      <span className="absolute top-2 right-2 w-2 h-2 bg-[#F59E0B] rounded-full"/>
    </button>
    <button onClick={() => nav('/app/education')} title="Help" aria-label="Help" className="w-10 h-10 rounded-full hover:bg-slate-100 hidden md:flex items-center justify-center" data-testid="topbar-help-btn"><HelpCircle className="w-5 h-5 text-slate-600"/></button>
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-2 hover:bg-slate-100 rounded-lg px-2 py-1.5 outline-none">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8BC53F] to-[#6FA82C] flex items-center justify-center text-white text-sm font-bold">
          {(user?.name || 'D').slice(0,1).toUpperCase()}
        </div>
        <div className="text-left hidden md:block">
          <div className="text-sm font-semibold text-slate-900 capitalize leading-tight">{user?.name || 'Demo'}</div>
          <div className="text-xs text-slate-500 leading-tight capitalize">{user?.role || 'Admin'}</div>
        </div>
        <ChevronDown className="w-4 h-4 text-slate-500"/>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Signed in as<div className="font-normal text-slate-500 text-xs">{user?.email}</div></DropdownMenuLabel>
        <DropdownMenuSeparator/>
        <DropdownMenuItem onClick={() => nav('/app/settings')}>Profile</DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav('/app/settings')}>Company Settings</DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav('/app/accounting/invoices')}>Billing</DropdownMenuItem>
        <DropdownMenuSeparator/>
        <DropdownMenuItem onClick={onLogout} className="text-red-600 focus:text-red-700"><LogOut className="w-4 h-4 mr-2"/>Sign Out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </header>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isDesktop = useIsDesktop();
  useReleaseStuckClicks();

  // Belt and braces: if the window grows past the breakpoint while the drawer is
  // open, drop it, so it can never be left mounted and waiting to reappear.
  useEffect(() => { if (isDesktop) setMobileOpen(false); }, [isDesktop]);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    const raw = localStorage.getItem('pp_user');
    if (!raw) { navigate('/login'); return; }
    let u;
    try { u = JSON.parse(raw); } catch { localStorage.removeItem('pp_user'); navigate('/login'); return; }
    if (!u || typeof u !== 'object') { localStorage.removeItem('pp_user'); navigate('/login'); return; }
    if (u.role === 'tech') { navigate('/tech'); return; }
    if (u.role === 'customer') { navigate('/portal'); return; }
    setUser(u);
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    const poll = () => messaging.unreadCount().then(d => setUnread(d.unread || 0)).catch(() => {});
    poll();
    const t = setInterval(poll, 10000);
    return () => clearInterval(t);
  }, [user]);

  const logout = () => { localStorage.removeItem('pp_user'); navigate('/'); };
  if (!user) return null;

  return (
    <div className="flex bg-slate-50 min-h-screen">
      <Sidebar collapsed={collapsed} unread={unread}/>
      <MobileDrawer open={mobileOpen} unread={unread} onClose={() => setMobileOpen(false)}/>
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar user={user} onLogout={logout} onToggle={() => (isDesktop ? setCollapsed(c => !c) : setMobileOpen(o => !o))}/>
        <main className="flex-1 min-w-0">
          <Routes>
            <Route path="dashboard" element={<DashboardHome user={user}/>}/>
            <Route path="schedule" element={<SchedulePage/>}/>
            <Route path="recurring" element={<ServicePlansPage/>}/>
            <Route path="pesticide" element={<PesticideLogPage/>}/>
            <Route path="devices" element={<DevicesPage/>}/>
            <Route path="audit" element={<AuditPackPage/>}/>
            <Route path="tracker" element={<ServiceTracker/>}/>
            <Route path="payments" element={<PaymentsPage/>}/>
            <Route path="collections" element={<CollectionsPage/>}/>
            <Route path="pest-library" element={<PestLibrary/>}/>
            <Route path="ai-assistant" element={<AiAssistant/>}/>
            <Route path="assistants" element={<AssistantsPanel/>}/>
            <Route path="end-of-day" element={<EndOfDay/>}/>
            <Route path="data" element={<DataHub/>}/>
            <Route path="knowledge" element={<KnowledgeAutoUpdate/>}/>
            <Route path="briefing" element={<BusinessBriefing/>}/>
            <Route path="instructions" element={<ServiceInstructions/>}/>
            <Route path="pesticide-reports" element={<PesticideReports/>}/>
            <Route path="map" element={<MapPage/>}/>
            <Route path="customers" element={<CustomersPage/>}/>
            <Route path="customers/:id" element={<CustomerDetail/>}/>
            <Route path="leads" element={<LeadsPage/>}/>
            <Route path="contracts" element={<ContractsPage/>}/>
            <Route path="forms" element={<FormsPage/>}/>
            <Route path="inventory" element={<InventoryPage/>}/>
            <Route path="messaging" element={<CommunicationHub/>}/>
            <Route path="education" element={<EducationPage/>}/>
            <Route path="routes" element={<RoutesPage/>}/>
            <Route path="invoices" element={<InvoicesQuickView/>}/>
            <Route path="reports" element={<ReportsPage/>}/>
            <Route path="marketing" element={<MarketingPage/>}/>
            <Route path="templates" element={<TemplatesPage/>}/>
            <Route path="settings" element={<SettingsPage/>}/>
            <Route path="accounting/*" element={<AccountingApp/>}/>
            <Route path="*" element={<DashboardHome user={user}/>}/>
          </Routes>
        </main>
      </div>
    </div>
  );
}
